import React, { useState, useEffect } from 'react';
// Remove unused icons
import { /* PencilIcon, TrashIcon, SendIcon, */ CalendarIcon, PlusIcon, RefreshCwIcon, WifiIcon } from 'lucide-react';
// Remove unused Card components
// import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'; 
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePosts, useDeletePost, usePostNow } from '@/hooks/usePosts';
import { useAuth } from '@/auth/AuthProvider';
// import { useNavigate } from 'react-router-dom'; // COMMENTED OUT
import { useToast } from '@/hooks/useToast';
import PostModal from './PostModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Post } from '@/services/api';
import PostList from './PostList';

/**
 * Sort posts by scheduled time
 * @param a First post
 * @param b Second post
 * @returns Sort order: -1 if a is before b, 1 if a is after b, 0 if equal
 */
const sortByScheduledTime = (a: Post, b: Post): number => {
  const dateA = new Date(a.scheduled_time);
  const dateB = new Date(b.scheduled_time);
  return dateA.getTime() - dateB.getTime();
};

const Dashboard: React.FC = () => {
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('unposted');

  // Configure usePosts with additional options to minimize unnecessary API calls
  const { status, data: posts, error: postsError, refetch } = usePosts({
    // Disable automatic refetching when this component is mounted
    refetchOnMount: false
  });
  
  const deletePost = useDeletePost();
  const postNow = usePostNow();
  const { isAuthenticated } = useAuth();
  // const navigate = useNavigate(); // COMMENTED OUT
  const { toast } = useToast();

  // Calculate filtered/sorted lists only when data is available
  // posts should hold previous data during refetch due to placeholderData
  const unpostedPosts = React.useMemo(() => 
    Array.isArray(posts) 
      ? posts.filter((post: Post) => post.status !== 'posted').sort(sortByScheduledTime)
      : [],
  [posts]);

  const postedPosts = React.useMemo(() =>
    Array.isArray(posts)
      ? posts.filter((post: Post) => post.status === 'posted').sort((a: Post, b: Post) => sortByScheduledTime(b, a))
      : [],
  [posts]);

  // UNCOMMENT HANDLERS
  const handleEditPost = (post: Post) => {
    console.log("Editing Post - ID:", post.id, "Data:", JSON.stringify(post));
    setSelectedPost(post.id);
    setIsPostModalOpen(true);
  };
  
  const handleDeletePost = async (post: Post) => {
    try {
      await deletePost.mutate(post.id);
      toast({ title: 'Post Deleted', description: 'The post has been successfully deleted.', type: 'success' });
    } catch (error) {
      toast({ title: 'Error Deleting Post', description: error instanceof Error ? error.message : 'Could not delete post.', type: 'error' });
    }
  };
  
  const handlePostNow = async (post: Post) => {
    try {
      await postNow.mutate(post.id);
      toast({ title: 'Post Sent', description: 'The post has been sent for immediate posting.', type: 'success' });
    } catch (error) {
      toast({ title: 'Error Sending Post', description: error instanceof Error ? error.message : 'Could not send post now.', type: 'error' });
    }
  };
  // END UNCOMMENT HANDLERS

  // Handle error state - show error but still try to render list if data exists
  useEffect(() => {
    if (status === 'error' && postsError) {
      toast({ 
        title: 'Background Refresh Failed',
        description: postsError.message || 'Could not refresh posts in the background.',
        type: 'error',
        duration: 5000
      });
      console.error("Dashboard: Background refresh error:", postsError);
    }
  }, [status, postsError, toast]);

  if (status === 'pending') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Render content if status is success OR if it's error but we have placeholder data
  return (status === 'success' || (status === 'error' && posts)) ? (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Posts</h1>
            {isAuthenticated && (
              <div className="ml-3 flex items-center text-emerald-600" title="Real-time updates active">
                <WifiIcon className="h-5 w-5" />
                <span className="ml-1 text-xs">Live</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="border-indigo-400 dark:border-indigo-500"
            >
              <RefreshCwIcon className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => {
                setSelectedPost(null);
                setIsPostModalOpen(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-base sm:text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
              disabled={deletePost.isPending || postNow.isPending}
            >
              <PlusIcon className="h-5 w-5" />
              New Post
            </Button>
          </div>
        </div>

        {/* Sort information message */}
        <div className="mb-4 text-sm text-muted-foreground flex items-center">
          <CalendarIcon className="h-4 w-4 mr-1" />
          <span>
            {activeTab === 'unposted' 
              ? 'Posts sorted by scheduled time (earliest first)' 
              : 'Posts sorted by posted time (most recent first)'}
          </span>
        </div>

        <Tabs defaultValue="unposted" onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full mb-6 grid grid-cols-2">
            <TabsTrigger 
              value="unposted" 
              className="text-base sm:text-lg py-3"
            >
              Pending Posts
              {unpostedPosts.length > 0 && (
                <span className="ml-2 bg-indigo-600 text-white text-sm px-2 py-0.5 rounded-full">
                  {unpostedPosts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="posted" 
              className="text-base sm:text-lg py-3"
            >
              Posted
              {postedPosts.length > 0 && (
                <span className="ml-2 bg-emerald-600 text-white text-sm px-2 py-0.5 rounded-full">
                  {postedPosts.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="unposted" className="mt-4">
            {unpostedPosts.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-card rounded-xl shadow-lg border-2 border-indigo-400 dark:border-indigo-500">
                <p className="text-muted-foreground text-base sm:text-lg">
                  No pending posts. Create your first post to get started!
                </p>
              </div>
            ) : (
              <PostList
                posts={unpostedPosts}
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onPostNow={handlePostNow}
              />
            )}
          </TabsContent>

          <TabsContent value="posted" className="mt-4">
            {postedPosts.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-card rounded-xl shadow-lg border-2 border-indigo-400 dark:border-indigo-500">
                <p className="text-muted-foreground text-base sm:text-lg">
                  No posted content yet.
                </p>
              </div>
            ) : (
              <PostList
                posts={postedPosts}
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onPostNow={handlePostNow}
              />
            )}
          </TabsContent>
        </Tabs>

        <PostModal
          isOpen={isPostModalOpen}
          onClose={() => {
            setIsPostModalOpen(false);
            setSelectedPost(null);
          }}
          post={selectedPost && Array.isArray(posts)
            ? posts.find((p: Post) => p.id === selectedPost) 
            : undefined}
        />
      </div>
    </div>
  ) : (
    // Only show full page error if status is error AND there's no placeholder data
    <Alert variant="destructive" className="m-4">
      <AlertDescription>
        {postsError?.message || 'Failed to load posts'}
      </AlertDescription>
    </Alert>
  );
};

export default Dashboard;